/**
 * The asset template library (piece 28; decisions 1292 and 1293): a node and
 * everything beneath it captured as structured data, so a tree built once can
 * be built again. 🔒 A template copies the shape and the settings a person
 * typed — node types, names, sort order, a component's own attribute values
 * and its maintenance schedules with their checklist template — and nothing
 * that belongs to one physical asset: no custody, no history, no serial
 * number or tag, no file or picture, no meter reading, no owner and no
 * generated Work Order.
 */
import { isAssetNodeType, type AssetNodeType } from './hierarchy';
import { PM_TRIGGER_TYPES, type PMTriggerType } from './pmSchedule';

export type AssetTemplateVisibility = 'AssetRecord' | 'WorkOrders' | 'Reports';

/** A component's own attribute as the template carries it: what was typed, never the derived reading. */
export interface AssetTemplateAttribute {
  attributeTypeId: string;
  unitId: string | null;
  enteredValue: string;
  attributeTypeValueId: string | null;
  notes: string | null;
  visibility: AssetTemplateVisibility;
}

/** A maintenance schedule as the template carries it: its trigger and its checklist template, never a generated period. */
export interface AssetTemplateSchedule {
  name: string;
  triggerType: PMTriggerType;
  intervalDays: number | null;
  meterAttributeTypeId: string | null;
  meterThreshold: number | null;
  checklistTemplateId: string;
  active: boolean;
}

/** One node of the captured subtree. `key` is its place in the list and `parentKey` its parent's; the root's is null. */
export interface AssetTemplateNode {
  key: number;
  parentKey: number | null;
  nodeType: AssetNodeType;
  name: string;
  sortOrder: number;
  attributes: AssetTemplateAttribute[];
  schedules: AssetTemplateSchedule[];
}

/** A node of the tree being captured, as the register stores it. */
export interface CapturableNode {
  id: string;
  parentNodeId: string | null;
  nodeType: AssetNodeType;
  name: string;
  sortOrder: number;
}

export interface CapturableAttribute extends AssetTemplateAttribute {
  assetNodeId: string;
}

export interface CapturableSchedule extends AssetTemplateSchedule {
  assetNodeId: string;
}

/**
 * 🔒 The subtree under `rootId`, root first and then each node before its
 * children, siblings in their stored order (decision 1292). 🔨 An attribute a
 * Meter schedule on the same node reads is a meter reading and is left out, so
 * a rebuilt schedule counts from the rebuilt asset's own first reading.
 * Empty where the root is not among the nodes.
 */
export function captureSubtree(
  rootId: string,
  nodes: readonly CapturableNode[],
  attributes: readonly CapturableAttribute[],
  schedules: readonly CapturableSchedule[],
): AssetTemplateNode[] {
  const root = nodes.find((node) => node.id === rootId);
  if (!root) return [];
  const byParent = new Map<string, CapturableNode[]>();
  for (const node of nodes) {
    if (!node.parentNodeId) continue;
    byParent.set(node.parentNodeId, [...(byParent.get(node.parentNodeId) ?? []), node]);
  }
  const out: AssetTemplateNode[] = [];
  const seen = new Set<string>();
  const visit = (node: CapturableNode, parentKey: number | null) => {
    if (seen.has(node.id)) return;
    seen.add(node.id);
    const key = out.length;
    const own = schedules.filter((row) => row.assetNodeId === node.id);
    const metered = new Set(own.filter((row) => row.triggerType === 'Meter' && row.meterAttributeTypeId).map((row) => row.meterAttributeTypeId));
    out.push({
      key,
      parentKey,
      nodeType: node.nodeType,
      name: node.name,
      sortOrder: node.sortOrder,
      attributes: node.nodeType === 'Component'
        ? attributes.filter((row) => row.assetNodeId === node.id && !metered.has(row.attributeTypeId)).map((row) => ({
            attributeTypeId: row.attributeTypeId, unitId: row.unitId, enteredValue: row.enteredValue, attributeTypeValueId: row.attributeTypeValueId, notes: row.notes, visibility: row.visibility,
          }))
        : [],
      schedules: own.map((row) => ({
        name: row.name, triggerType: row.triggerType, intervalDays: row.intervalDays, meterAttributeTypeId: row.meterAttributeTypeId, meterThreshold: row.meterThreshold, checklistTemplateId: row.checklistTemplateId, active: row.active,
      })),
    });
    const children = [...(byParent.get(node.id) ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    for (const child of children) visit(child, key);
  };
  visit(root, null);
  return out;
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);
const VISIBILITIES: readonly string[] = ['AssetRecord', 'WorkOrders', 'Reports'];

/**
 * Why a stored subtree cannot be built, or null where it can. 🔒 The same
 * hierarchy rules a hand-built tree meets: one root and it comes first, every
 * parent named comes before its child, a Component holds nothing beneath it,
 * every node has a name, and only a Component carries its own attributes.
 */
export function templateShapeProblem(nodes: unknown): string | null {
  if (!Array.isArray(nodes) || nodes.length === 0) return 'the template holds no nodes';
  const types = new Map<number, AssetNodeType>();
  for (const [index, raw] of nodes.entries()) {
    if (!isRecord(raw)) return `node ${index + 1} of the template is not a node`;
    const { key, parentKey, nodeType, name, sortOrder, attributes, schedules } = raw;
    if (key !== index) return `node ${index + 1} of the template is out of its order`;
    if (!isAssetNodeType(nodeType)) return `node ${index + 1} of the template has no level the register knows`;
    if (typeof name !== 'string' || name.trim() === '') return `node ${index + 1} of the template has no name`;
    if (!Number.isInteger(sortOrder) || (sortOrder as number) < 0) return `node ${index + 1} of the template has no position`;
    if (index === 0) {
      if (parentKey !== null) return 'the template’s first node is its root and sits beneath nothing inside it';
    } else {
      if (typeof parentKey !== 'number' || !types.has(parentKey)) return `node ${index + 1} of the template names a parent that does not come before it`;
      if (types.get(parentKey) === 'Component') return 'a Component is the leaf of the tree and holds nothing beneath it; place the node under a grouping instead';
    }
    if (!Array.isArray(attributes) || !Array.isArray(schedules)) return `node ${index + 1} of the template carries no attribute or schedule list`;
    if (attributes.length > 0 && nodeType !== 'Component') return 'only a Component carries its own attributes';
    for (const attribute of attributes) {
      if (!isRecord(attribute) || typeof attribute.attributeTypeId !== 'string' || typeof attribute.enteredValue !== 'string' || !VISIBILITIES.includes(String(attribute.visibility))) return `an attribute on node ${index + 1} of the template is not one the register can write`;
    }
    for (const schedule of schedules) {
      if (!isRecord(schedule) || typeof schedule.name !== 'string' || typeof schedule.checklistTemplateId !== 'string' || !(PM_TRIGGER_TYPES as readonly string[]).includes(String(schedule.triggerType))) return `a schedule on node ${index + 1} of the template is not one the register can write`;
    }
    types.set(index, nodeType);
  }
  return null;
}

/** How many nodes a stored subtree builds, which the templates page shows beside each. */
export function templateNodeCount(nodes: unknown): number {
  return Array.isArray(nodes) ? nodes.length : 0;
}

/** Why a template cannot take this name, or null where it can; two active templates never share one. */
export function templateNameProblem(name: string | null | undefined): string | null {
  return (name ?? '').trim() === '' ? 'a template has a name' : null;
}

/** 🔒 The Asset Templates page, under Assets (decision 1293). A literal segment beside `/assets/:id`, which never names a node. */
export const ASSET_TEMPLATES_ROUTE = '/assets/templates';

/** One row of the templates page. */
export interface AssetTemplateRow {
  id: string;
  version: number;
  name: string;
  description: string | null;
  nodeCount: number;
  rootType: AssetNodeType | null;
  rootName: string | null;
  savedByName: string;
  savedAt: string;
  isRetired: boolean;
}

export interface AssetTemplateListAnswer {
  rows: AssetTemplateRow[];
  mayWrite: boolean;
}
